// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The maths behind "Sky above me" (docs/AYNI_XR_SATELLITES_PLAN.md): where
 * a satellite stands in the viewer's sky (azimuth from north, elevation
 * over the horizon), whether sunlight reaches it, and which way a phone's
 * camera faces by its compass. Spherical Earth throughout (6371 km): the
 * error is a fraction of a degree, far below what SGP4 mean elements give.
 */
import { GLOBE_RADIUS_KM } from './satelliteOrbit'

const DEG = Math.PI / 180

export interface Observer {
  latDeg: number
  lonDeg: number
}

/** Observer frame: position (km, Earth-fixed) and east/north/up unit vectors. */
export interface ObserverFrame {
  o: [number, number, number]
  e: [number, number, number]
  n: [number, number, number]
  u: [number, number, number]
}

export function observerFrame(obs: Observer): ObserverFrame {
  const la = obs.latDeg * DEG
  const lo = obs.lonDeg * DEG
  const cl = Math.cos(la)
  const sl = Math.sin(la)
  const co = Math.cos(lo)
  const so = Math.sin(lo)
  const u: [number, number, number] = [cl * co, cl * so, sl]
  return {
    o: [GLOBE_RADIUS_KM * u[0], GLOBE_RADIUS_KM * u[1], GLOBE_RADIUS_KM * u[2]],
    e: [-so, co, 0],
    n: [-sl * co, -sl * so, cl],
    u,
  }
}

export interface LookAngles {
  /** Clockwise from north, radians. */
  az: number
  /** Over the horizon, radians (negative below). */
  el: number
  rangeKm: number
}

export function lookAngles(f: ObserverFrame, x: number, y: number, z: number): LookAngles {
  const rx = x - f.o[0]
  const ry = y - f.o[1]
  const rz = z - f.o[2]
  const range = Math.hypot(rx, ry, rz)
  const e = rx * f.e[0] + ry * f.e[1] + rz * f.e[2]
  const n = rx * f.n[0] + ry * f.n[1] + rz * f.n[2]
  const u = rx * f.u[0] + ry * f.u[1] + rz * f.u[2]
  return { az: Math.atan2(e, n), el: Math.asin(Math.max(-1, Math.min(1, u / range))), rangeKm: range }
}

/** Julian day number of a time in ms since 1970. */
export function julianDay(ms: number): number {
  return ms / 86_400_000 + 2440587.5
}

/** Greenwich mean sidereal time, radians in [0, 2π): how far the sky has turned past the equinox. */
export function gmst(ms: number): number {
  const n = julianDay(ms) - 2451545.0
  const turn = 2 * Math.PI
  return ((((280.46061837 + 360.98564736629 * n) * DEG) % turn) + turn) % turn
}

/** The Sun's right ascension and declination, radians (low-precision almanac, ~0.01°). */
export function sunRaDec(ms: number): { ra: number; dec: number } {
  const n = julianDay(ms) - 2451545.0
  const L = (280.46 + 0.9856474 * n) * DEG
  const g = (357.528 + 0.9856003 * n) * DEG
  const lambda = L + (1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * DEG
  const eps = (23.439 - 0.0000004 * n) * DEG
  return {
    ra: Math.atan2(Math.cos(eps) * Math.sin(lambda), Math.cos(lambda)),
    dec: Math.asin(Math.sin(eps) * Math.sin(lambda)),
  }
}

/**
 * A direction on the celestial sphere as a unit vector in the Earth-centred
 * inertial frame: x to the equinox (RA 0), z to the north celestial pole.
 */
export function eciDirection(ra: number, dec: number): [number, number, number] {
  return [Math.cos(dec) * Math.cos(ra), Math.cos(dec) * Math.sin(ra), Math.sin(dec)]
}

/** The Sun's direction from Earth's centre, Earth-fixed, unit vector (low-precision almanac, ~0.01°). */
export function sunDirectionEcf(ms: number): [number, number, number] {
  const { ra, dec } = sunRaDec(ms)
  return eciDirection(ra - gmst(ms), dec)
}

/** Row-major 3×3 product a·b. */
export function multiply3(a: readonly number[], b: readonly number[]): number[] {
  const out = new Array<number>(9)
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      out[r * 3 + c] = a[r * 3] * b[c] + a[r * 3 + 1] * b[3 + c] + a[r * 3 + 2] * b[6 + c]
    }
  }
  return out
}

/**
 * The globe mesh's local frame from Earth-fixed (row-major 3×3): photorealEarth
 * puts latitude φ, longitude λ at (cos φ cos λ, sin φ, −cos φ sin λ), so local =
 * (x, z, −y) of Earth-fixed.
 */
export const GLOBE_LOCAL_FROM_ECEF: readonly number[] = [1, 0, 0, 0, 0, 1, 0, -1, 0]

/**
 * AYNI (owner, 2026-11-03, Fully VR as floating in space): the matrix
 * (row-major 3×3) that takes a celestial direction (inertial, see
 * {@link eciDirection}) into the globe mesh's local frame at sidereal angle
 * `gmstRad`: inertial → Earth-fixed (a turn of −gmst about the pole), then
 * {@link GLOBE_LOCAL_FROM_ECEF}. The globe's own turn (its quaternion) then
 * takes the whole sky with it, so the globe and the stars are one system,
 * with no viewer's place, north or horizon in it.
 */
export function celestialToGlobeLocal(gmstRad: number): number[] {
  const cg = Math.cos(gmstRad)
  const sg = Math.sin(gmstRad)
  const toEcf = [cg, sg, 0, -sg, cg, 0, 0, 0, 1]
  return multiply3(GLOBE_LOCAL_FROM_ECEF, toEcf)
}

/** Applies a row-major 3×3 matrix to a vector. */
export function apply3(m: readonly number[], v: readonly [number, number, number]): [number, number, number] {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
  ]
}

/** True when Earth's shadow (a cylinder behind it) covers a point, km Earth-fixed. */
export function inEarthShadow(x: number, y: number, z: number, sun: [number, number, number]): boolean {
  const along = x * sun[0] + y * sun[1] + z * sun[2]
  if (along >= 0) return false
  const px = x - along * sun[0]
  const py = y - along * sun[1]
  const pz = z - along * sun[2]
  return Math.hypot(px, py, pz) < GLOBE_RADIUS_KM
}

/** The Sun's elevation for the observer, radians. */
export function sunElevation(f: ObserverFrame, sun: [number, number, number]): number {
  return Math.asin(sun[0] * f.u[0] + sun[1] * f.u[1] + sun[2] * f.u[2])
}

/**
 * Compass heading (radians, clockwise from north) of the direction a phone's
 * back camera faces, from a `deviceorientationabsolute` reading (degrees).
 * W3C: R = Rz(α)·Rx(β)·Ry(γ), Earth frame x east, y north, z up; the
 * camera looks down the device's −z.
 */
export function cameraHeading(alphaDeg: number, betaDeg: number, gammaDeg: number): number {
  const a = alphaDeg * DEG
  const b = betaDeg * DEG
  const g = gammaDeg * DEG
  const ca = Math.cos(a)
  const sa = Math.sin(a)
  const cb = Math.cos(b)
  const sb = Math.sin(b)
  const cg = Math.cos(g)
  const sg = Math.sin(g)
  // Third column of R, negated: R·(0, 0, −1).
  const vx = -(ca * sg + sa * sb * cg)
  const vy = -(sa * sg - ca * sb * cg)
  return Math.atan2(vx, vy)
}

/** Yaw in three.js/WebXR space (radians, clockwise seen from above, 0 = −z) of a horizontal direction. */
export function xrYaw(dx: number, dz: number): number {
  return Math.atan2(dx, -dz)
}

export interface Pass {
  /** When it first climbs over `minEl`. */
  riseMs: number
  /** Its highest elevation in that pass, radians. */
  maxEl: number
  /** Where it climbs over `minEl`: azimuth clockwise from north, radians. */
  riseAz: number
}

/** The first pass over `minElDeg` in a path sampled every `stepMs` from `startMs` (Earth-fixed km). */
export function nextPass(f: ObserverFrame, path: Float32Array, startMs: number, stepMs: number, minElDeg = 10): Pass | null {
  const min = minElDeg * DEG
  let rise = -1
  let riseAz = 0
  let maxEl = -Infinity
  for (let s = 0; s * 3 < path.length; s++) {
    const x = path[s * 3]
    if (x !== x) continue
    const { el, az } = lookAngles(f, x, path[s * 3 + 1], path[s * 3 + 2])
    if (el >= min) {
      if (rise < 0) {
        rise = s
        riseAz = az
      }
      maxEl = Math.max(maxEl, el)
    } else if (rise >= 0) break
  }
  return rise < 0 ? null : { riseMs: startMs + rise * stepMs, maxEl, riseAz }
}

/** The eight compass points, by azimuth (radians clockwise from north): 0 N, 1 NE … 7 NW. */
export function compassPoint(az: number): number {
  const turn = ((az / (2 * Math.PI)) % 1 + 1) % 1
  return Math.round(turn * 8) % 8
}

/**
 * The geostationary belt as the viewer sees it: Earth-fixed km of points on
 * the 42,164 km equatorial ring, every `stepDeg` of longitude.
 */
export function clarkeBelt(stepDeg = 1): Float32Array {
  const r = 42164
  const n = Math.round(360 / stepDeg)
  const out = new Float32Array(n * 3)
  for (let i = 0; i < n; i++) {
    const lon = (i * stepDeg * Math.PI) / 180
    out[i * 3] = r * Math.cos(lon)
    out[i * 3 + 1] = r * Math.sin(lon)
    out[i * 3 + 2] = 0
  }
  return out
}
